import React, { useState } from 'react';
import { Lead } from '../types';
import { CheckCircle2, Circle, Clock, Plus, Trash2, Save } from 'lucide-react';

interface SalesPipelineProps {
  leads: Lead[];
  onUpdateLeads: (leads: Lead[]) => void;
}

const SalesPipeline: React.FC<SalesPipelineProps> = ({ leads, onUpdateLeads }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newLead, setNewLead] = useState<Partial<Lead>>({
    name: '',
    company: '',
    value: 490,
    status: 'New',
    notes: ''
  });

  const handleStatusChange = (id: string, newStatus: Lead['status']) => {
    const updatedLeads = leads.map(lead => 
      lead.id === id ? { ...lead, status: newStatus } : lead
    );
    onUpdateLeads(updatedLeads);
  };

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to remove this opportunity?')) {
        const updatedLeads = leads.filter(lead => lead.id !== id);
        onUpdateLeads(updatedLeads);
    }
  };

  const handleAddLead = () => {
    if (!newLead.name || !newLead.company) return;
    
    const leadToAdd: Lead = {
      id: Date.now().toString(),
      name: newLead.name,
      company: newLead.company,
      value: newLead.value || 490,
      status: 'New',
      notes: newLead.notes || ''
    };

    onUpdateLeads([...leads, leadToAdd]);
    setIsAdding(false);
    setNewLead({ name: '', company: '', value: 490, status: 'New', notes: '' });
  };

  return (
    <div className="animate-fade-in pb-12">
       <header className="mb-8 flex justify-between items-end">
        <div>
            <h2 className="text-3xl font-bold text-white tracking-tight">Sales Pipeline</h2>
            <p className="text-slate-400">Manage your high-ticket opportunities</p>
        </div>
        <button 
            onClick={() => setIsAdding(!isAdding)}
            className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-colors"
        >
            <Plus className="w-4 h-4 mr-2" />
            Add Opportunity
        </button>
      </header>

      {/* Add Lead Form */}
      {isAdding && (
        <div className="bg-slate-800/50 border border-blue-500/50 rounded-xl p-4 mb-6 animate-fade-in">
            <h3 className="text-sm font-bold text-blue-400 mb-3 uppercase tracking-wider">New Lead Entry</h3>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <input 
                    type="text" 
                    placeholder="Contact Name"
                    className="bg-slate-900 border border-slate-700 rounded p-2 text-sm text-white focus:border-blue-500 outline-none"
                    value={newLead.name}
                    onChange={e => setNewLead({...newLead, name: e.target.value})}
                />
                <input 
                    type="text" 
                    placeholder="Company"
                    className="bg-slate-900 border border-slate-700 rounded p-2 text-sm text-white focus:border-blue-500 outline-none"
                    value={newLead.company}
                    onChange={e => setNewLead({...newLead, company: e.target.value})}
                />
                 <div className="relative">
                    <span className="absolute left-2 top-2 text-slate-500 text-sm">€</span>
                    <input 
                        type="number" 
                        placeholder="Value"
                        className="bg-slate-900 border border-slate-700 rounded p-2 pl-6 text-sm text-white focus:border-blue-500 outline-none w-full"
                        value={newLead.value}
                        onChange={e => setNewLead({...newLead, value: Number(e.target.value)})}
                    />
                 </div>
                <input 
                    type="text" 
                    placeholder="Notes"
                    className="bg-slate-900 border border-slate-700 rounded p-2 text-sm text-white focus:border-blue-500 outline-none"
                    value={newLead.notes}
                    onChange={e => setNewLead({...newLead, notes: e.target.value})}
                />
                <button 
                    onClick={handleAddLead}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white rounded p-2 text-sm font-medium flex items-center justify-center"
                >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                </button>
            </div>
        </div>
      )}
      
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden shadow-lg">
         <table className="w-full text-left border-collapse">
            <thead>
                <tr className="bg-slate-900/50 border-b border-slate-700 text-xs text-slate-400 uppercase tracking-wider">
                    <th className="p-4">Contact</th>
                    <th className="p-4">Company</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Monthly Value</th>
                    <th className="p-4">Notes</th>
                    <th className="p-4 w-10"></th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-700 text-sm">
                {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-slate-700/50 transition-colors group">
                        <td className="p-4 font-medium text-white">{lead.name}</td>
                        <td className="p-4 text-slate-300">{lead.company}</td>
                        <td className="p-4">
                            <select 
                                value={lead.status}
                                onChange={(e) => handleStatusChange(lead.id, e.target.value as any)}
                                className={`bg-transparent border-none text-xs font-medium focus:ring-0 cursor-pointer ${
                                    lead.status === 'Closed Won' ? 'text-emerald-400' :
                                    lead.status === 'Demo Booked' ? 'text-blue-400' :
                                    'text-slate-400'
                                }`}
                            >
                                <option value="New" className="bg-slate-800 text-slate-400">New</option>
                                <option value="Qualified" className="bg-slate-800 text-slate-400">Qualified</option>
                                <option value="Demo Booked" className="bg-slate-800 text-blue-400">Demo Booked</option>
                                <option value="Proposal" className="bg-slate-800 text-purple-400">Proposal</option>
                                <option value="Closed Won" className="bg-slate-800 text-emerald-400">Closed Won</option>
                            </select>
                        </td>
                        <td className="p-4 text-slate-300 font-mono">€{lead.value}</td>
                        <td className="p-4 text-slate-400 max-w-xs truncate">{lead.notes}</td>
                        <td className="p-4 text-right">
                            <button 
                                onClick={() => handleDelete(lead.id)}
                                className="text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </td>
                    </tr>
                ))}
            </tbody>
         </table>
      </div>
    </div>
  );
};

export default SalesPipeline;